import {
  resolveApolloAccountId,
  resolveHubSpotObjectId,
  type Company,
  type EntityCrm,
  type MergedAccountView,
  type Account,
} from "@sales-ops-amplify/shared";

export type ProfileField = {
  key: string;
  label: string;
  value: string;
  href?: string;
};

function str(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "number" && Number.isFinite(value)) {
    return value.toLocaleString();
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  const s = String(value).trim();
  return s || undefined;
}

function joinList(
  values?: Array<string | number> | string | null,
  limit?: number,
): string | undefined {
  const arr = Array.isArray(values)
    ? values
    : typeof values === "string"
      ? values
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean)
      : [];
  if (!arr.length) return undefined;
  const items = arr.map((v) => String(v).trim()).filter(Boolean);
  if (!items.length) return undefined;
  if (limit != null && items.length > limit) {
    return `${items.slice(0, limit).join(", ")} +${items.length - limit}`;
  }
  return items.join(", ");
}

function formatAddress(
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  } | null,
): string | undefined {
  if (!address) return undefined;
  const line1 = address.street;
  const line2 = [address.city, address.state, address.postalCode]
    .filter(Boolean)
    .join(", ");
  const parts = [line1, line2, address.country].filter(Boolean);
  return parts.length ? parts.join(" · ") : undefined;
}

function formatDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = Date.parse(iso);
  if (Number.isNaN(d)) return iso;
  return new Date(d).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function asHttpsUrl(raw?: string): string | undefined {
  if (!raw?.trim()) return undefined;
  const value = raw.trim();
  if (/^https?:\/\//i.test(value)) return value;
  if (value.includes(".") && !value.includes(" ")) {
    return `https://${value.replace(/^\/\//, "")}`;
  }
  return value;
}

/** Prefer user CRM shell, then directory catalog — or directory-only for Find companies. */
export function coalesceCompanyProfile(
  merged: MergedAccountView,
  opts?: { preferDirectory?: boolean },
) {
  const user = opts?.preferDirectory ? undefined : merged.user;
  const directory = merged.directory;
  const crm: EntityCrm | undefined = user?.crm ?? directory?.crm;

  const domain = user?.domain ?? directory?.domain;
  const industry = user?.industry ?? directory?.industry;
  const description = user?.description ?? directory?.description;
  const phone = user?.phone ?? directory?.phone;
  const employeeCount = user?.employeeCount ?? directory?.employeeCount;
  const size = directory?.size;
  const linkedinUrl =
    user?.linkedinUrl ?? directory?.socialMedia?.linkedin ?? undefined;
  const lifecycleStage = user?.lifecycleStage ?? directory?.accountStage;
  const ownerName = user?.ownerName;
  const address =
    formatAddress(user?.address) ??
    directory?.headquarters ??
    (directory
      ? [directory.hqCity, directory.hqRegion, directory.hqCountry].filter(Boolean).join(", ") ||
        undefined
      : undefined);

  const website =
    user?.url ??
    directory?.url ??
    directory?.website ??
    (domain ? `https://${domain}` : undefined);

  return {
    user,
    directory,
    crm,
    displayName: merged.name,
    domain,
    industry,
    description,
    phone,
    employeeCount,
    size,
    linkedinUrl,
    lifecycleStage,
    ownerName,
    address,
    website,
    foundedYear: user?.foundedYear ?? directory?.foundedYear,
    hqCountry: directory?.hqCountry ?? user?.country ?? user?.address?.country,
    annualRevenue: user?.annualRevenue ?? directory?.annualRevenue,
    totalFunding: user?.totalFunding ?? directory?.totalFunding,
    latestFunding: user?.latestFunding ?? directory?.latestFunding,
    latestFundingAmount: directory?.latestFundingAmount,
    keywords: user?.keywords ?? directory?.keywords,
    technologies: user?.technologies ?? directory?.technologies,
    sicCodes: directory?.sicCodes,
    naicsCodes: directory?.naicsCodes,
    parentCompany: directory?.parentCompany,
    parentCompanyId: directory?.parentCompanyId,
    accountStage: directory?.accountStage,
    status: user?.status,
    priority: user?.priority,
    socialMedia: directory?.socialMedia,
  };
}

export type CoalescedCompanyProfile = ReturnType<typeof coalesceCompanyProfile>;

/** Primary fact tiles — keep the strip sparse. */
export function companyPrimaryFacts(
  profile: CoalescedCompanyProfile,
): ProfileField[] {
  const employees =
    profile.employeeCount != null
      ? str(profile.employeeCount)
      : profile.size
        ? str(profile.size)
        : undefined;

  const fields: Array<ProfileField | null> = [
    profile.domain
      ? {
          key: "domain",
          label: "Domain",
          value: profile.domain,
          href: asHttpsUrl(profile.domain),
        }
      : null,
    profile.address
      ? { key: "hq", label: "HQ", value: profile.address }
      : null,
    employees
      ? {
          key: "employees",
          label: profile.employeeCount != null ? "Employees" : "Size",
          value: employees,
        }
      : null,
    profile.phone
      ? {
          key: "phone",
          label: "Phone",
          value: profile.phone,
          href: `tel:${profile.phone.replace(/\s+/g, "")}`,
        }
      : null,
    profile.lifecycleStage
      ? {
          key: "lifecycle",
          label: "Lifecycle",
          value: profile.lifecycleStage,
        }
      : null,
    profile.foundedYear
      ? {
          key: "founded",
          label: "Founded",
          value: String(profile.foundedYear),
        }
      : null,
  ];

  return fields.filter((f): f is ProfileField => Boolean(f));
}

/** Common secondary fields shown in the profile card. */
export function companyCommonFields(
  profile: CoalescedCompanyProfile,
): ProfileField[] {
  const fields: Array<ProfileField | null> = [
    profile.industry
      ? { key: "industry", label: "Industry", value: profile.industry }
      : null,
    profile.ownerName
      ? { key: "owner", label: "Owner", value: profile.ownerName }
      : null,
    profile.status
      ? { key: "status", label: "Status", value: profile.status }
      : null,
    profile.priority
      ? { key: "priority", label: "Priority", value: profile.priority }
      : null,
    profile.linkedinUrl
      ? {
          key: "linkedin",
          label: "LinkedIn",
          value: "Company page",
          href: asHttpsUrl(profile.linkedinUrl),
        }
      : null,
    profile.annualRevenue != null
      ? {
          key: "revenue",
          label: "Annual revenue",
          value: str(profile.annualRevenue)!,
        }
      : null,
    joinList(profile.technologies, 6)
      ? {
          key: "technologies",
          label: "Technologies",
          value: joinList(profile.technologies, 6)!,
        }
      : null,
    joinList(profile.keywords, 8)
      ? {
          key: "keywords",
          label: "Keywords",
          value: joinList(profile.keywords, 8)!,
        }
      : null,
  ];

  return fields.filter((f): f is ProfileField => Boolean(f));
}

/** Remaining / less common fields + CRM sync metadata for the reveal panel. */
export function companyExtendedFields(
  profile: CoalescedCompanyProfile,
): ProfileField[] {
  const hubspotId = resolveHubSpotObjectId(
    profile.crm,
    (profile.user as unknown as Record<string, unknown> | undefined)?.hubspotCompanyId as string | undefined,
  );
  const apolloId = resolveApolloAccountId(
    profile.crm,
    ((profile.user as unknown as Record<string, unknown> | undefined)?.apolloAccountId as string | undefined) ?? (((profile.directory) as unknown as Record<string, unknown> | undefined)?.apolloAccountId as string | undefined),
  );
  const origin = profile.crm?.origin ?? ((profile.user as unknown as Record<string, unknown> | undefined)?.syncOrigin as string | undefined);
  const syncedAt =
    formatDate(profile.crm?.hubspotSyncedAt) ??
    formatDate((profile.user as unknown as Record<string, unknown> | undefined)?.hubspotSyncedAt as string | undefined);
  const apolloSyncedAt = formatDate(profile.crm?.apolloSyncedAt);
  const remoteUpdated = formatDate(profile.crm?.lastRemoteUpdatedAt);

  const socialExtra = Object.entries(profile.socialMedia ?? {})
    .filter(([k, v]) => k !== "linkedin" && Boolean(v))
    .map(([k, v]) => `${k}: ${v}`);

  const fields: Array<ProfileField | null> = [
    profile.hqCountry
      ? { key: "country", label: "Country", value: profile.hqCountry }
      : null,
    profile.accountStage && profile.accountStage !== profile.lifecycleStage
      ? {
          key: "accountStage",
          label: "Account stage",
          value: profile.accountStage,
        }
      : null,
    profile.totalFunding != null
      ? {
          key: "totalFunding",
          label: "Total funding",
          value: str(profile.totalFunding)!,
        }
      : null,
    profile.latestFunding
      ? {
          key: "latestFunding",
          label: "Latest funding",
          value: profile.latestFundingAmount
            ? `${profile.latestFunding} (${str(profile.latestFundingAmount)})`
            : profile.latestFunding,
        }
      : null,
    joinList(profile.sicCodes)
      ? { key: "sic", label: "SIC codes", value: joinList(profile.sicCodes)! }
      : null,
    joinList(profile.naicsCodes)
      ? {
          key: "naics",
          label: "NAICS codes",
          value: joinList(profile.naicsCodes)!,
        }
      : null,
    profile.parentCompany
      ? {
          key: "parent",
          label: "Parent company",
          value: profile.parentCompany,
        }
      : null,
    socialExtra.length
      ? {
          key: "social",
          label: "Other social",
          value: socialExtra.join(" · "),
        }
      : null,
    origin
      ? { key: "syncOrigin", label: "Sync origin", value: origin }
      : null,
    hubspotId
      ? { key: "hubspotId", label: "HubSpot company ID", value: hubspotId }
      : null,
    apolloId
      ? { key: "apolloId", label: "Apollo account ID", value: apolloId }
      : null,
    profile.crm?.hubspot?.ownerId
      ? {
          key: "hsOwner",
          label: "HubSpot owner ID",
          value: profile.crm.hubspot.ownerId,
        }
      : null,
    syncedAt
      ? { key: "hsSynced", label: "HubSpot synced", value: syncedAt }
      : null,
    apolloSyncedAt
      ? { key: "apSynced", label: "Apollo synced", value: apolloSyncedAt }
      : null,
    remoteUpdated
      ? {
          key: "remoteUpdated",
          label: "Remote updated",
          value: remoteUpdated,
        }
      : null,
  ];

  return fields.filter((f): f is ProfileField => Boolean(f));
}

export function companyCrmPropertyBags(profile: CoalescedCompanyProfile): Array<{
  source: "HubSpot" | "Apollo";
  properties: Record<string, unknown>;
}> {
  const bags: Array<{
    source: "HubSpot" | "Apollo";
    properties: Record<string, unknown>;
  }> = [];
  if (profile.crm?.hubspot?.properties && Object.keys(profile.crm.hubspot.properties).length) {
    bags.push({
      source: "HubSpot",
      properties: profile.crm.hubspot.properties as unknown as Record<string, unknown>,
    });
  }
  if (profile.crm?.apollo?.properties && Object.keys(profile.crm.apollo.properties).length) {
    bags.push({
      source: "Apollo",
      properties: profile.crm.apollo.properties,
    });
  }
  return bags;
}

export function hasCompanyCrmSync(user?: Account, global?: Company): boolean {
  return Boolean(
    resolveHubSpotObjectId(user?.crm, (user as unknown as Record<string, unknown> | undefined)?.hubspotCompanyId as string | undefined) ||
      resolveApolloAccountId(
        user?.crm ?? global?.crm,
        ((user as unknown as Record<string, unknown> | undefined)?.apolloAccountId as string | undefined) ?? ((global as unknown as Record<string, unknown> | undefined)?.apolloAccountId as string | undefined),
      ),
  );
}
